/**
 * Multi-date / linked occurrences — aligned with meeteor-web `lib/eventOccurrenceDates.ts`.
 * Widget uses this to show the same “Select date” flow as the public event page.
 */

export type EventOccurrenceEntry = {
  iso: string;
  /** When set, choosing this row should load another event (parent can swap embed / navigate). */
  targetEventId?: string;
};

function linkedEventUrlSegment(row: Record<string, unknown>): string | undefined {
  const idLike =
    row.event_id ??
    row.eventId ??
    row.event_code ??
    row.code ??
    row.id ??
    row._id;
  const fromIds =
    typeof idLike === 'string' && idLike.trim()
      ? idLike.trim()
      : idLike != null && String(idLike).trim()
        ? String(idLike).trim()
        : undefined;
  const slugOnly = typeof row.slug === 'string' && row.slug.trim() ? row.slug.trim() : undefined;
  return fromIds ?? slugOnly;
}

function utcDayKey(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

/** Same calendar day in UTC (for comparing occurrence picks). */
export function sameUtcCalendarDay(aIso: string, bIso: string): boolean {
  return utcDayKey(aIso) === utcDayKey(bIso);
}

/**
 * If two rows fall on the same calendar day, keep the one with `targetEventId` (linked child / slug) when the other has none
 * so we still refetch the correct `event` document for that night.
 */
function mergeByDay(preferred: Map<string, EventOccurrenceEntry>, e: EventOccurrenceEntry): void {
  const day = utcDayKey(e.iso);
  const prev = preferred.get(day);
  if (!prev) {
    preferred.set(day, e);
    return;
  }
  const pHas = Boolean(prev.targetEventId);
  const nHas = Boolean(e.targetEventId);
  if (nHas && !pHas) {
    preferred.set(day, e);
    return;
  }
  if (nHas && pHas) {
    preferred.set(day, e);
  }
  // keep prev if it has target and new does not, or both lack target (first wins)
}

function pushFromItem(item: unknown, out: string[]): void {
  if (item == null) return;
  if (typeof item === 'string' && item.trim()) {
    const d = new Date(item.trim());
    if (!Number.isNaN(d.getTime())) out.push(d.toISOString());
    return;
  }
  if (typeof item !== 'object') return;
  const o = item as Record<string, unknown>;
  const raw = o.date ?? o.start_date ?? o.event_start_date ?? o.starts_at ?? o.start ?? o.datetime;
  if (typeof raw === 'string' && raw.trim()) {
    const d = new Date(raw.trim());
    if (!Number.isNaN(d.getTime())) out.push(d.toISOString());
  }
}

function collectFromArray(value: unknown): string[] {
  const out: string[] = [];
  if (!Array.isArray(value)) return out;
  for (const item of value) pushFromItem(item, out);
  return out;
}

function mergeEventSources(event: Record<string, unknown>): string[] {
  const d = event.details as Record<string, unknown> | undefined;
  return [
    ...collectFromArray(event.event_dates),
    ...collectFromArray(event.eventDates),
    ...collectFromArray(event.occurrence_dates),
    ...collectFromArray(event.occurrences),
    ...collectFromArray(event.dates),
    ...collectFromArray(d?.event_dates),
    ...collectFromArray(d?.dates),
    ...collectFromArray(d?.occurrence_dates),
  ];
}

/** Extract a calendar date (ISO) from a loose object row. */
function isoFromObjectRow(o: Record<string, unknown>): string | null {
  const raw = o.date ?? o.start_date ?? o.event_start_date ?? o.starts_at ?? o.start ?? o.datetime;
  if (typeof raw === 'string' && raw.trim()) {
    const d = new Date(raw.trim());
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return null;
}

/**
 * `event_id` / `slug` on an occurrence object — different from the current doc → switch loaded event.
 */
function targetKeyFromObjectRow(
  o: Record<string, unknown>,
  currentEventId: string,
  currentSlug: string,
): string | undefined {
  for (const k of ['event_id', 'eventId', 'child_event_id'] as const) {
    const v = o[k];
    if (typeof v === 'string' && v.trim() && v.trim() !== currentEventId) return v.trim();
  }
  const s = typeof o.slug === 'string' ? o.slug.trim() : '';
  if (s && s !== currentSlug) return s;
  return undefined;
}

function walkObjectArrayForOccurrences(
  value: unknown,
  currentEventId: string,
  currentSlug: string,
  out: EventOccurrenceEntry[],
): void {
  if (!Array.isArray(value)) return;
  for (const item of value) {
    if (item == null || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const iso = isoFromObjectRow(o);
    if (!iso) continue;
    const targetEventId = targetKeyFromObjectRow(o, currentEventId, currentSlug);
    out.push({ iso, targetEventId });
  }
}

function collectObjectOccurrenceRows(
  event: Record<string, unknown>,
  currentEventId: string,
  currentSlug: string,
): EventOccurrenceEntry[] {
  const d = event.details as Record<string, unknown> | undefined;
  const out: EventOccurrenceEntry[] = [];
  const arrays = [
    event.event_dates,
    event.eventDates,
    event.occurrence_dates,
    event.occurrences,
    event.dates,
    d?.event_dates,
    d?.dates,
    d?.occurrence_dates,
  ];
  for (const a of arrays) {
    walkObjectArrayForOccurrences(a, currentEventId, currentSlug, out);
  }
  const br = d?.event_date_breakdown || event.event_date_breakdown;
  if (Array.isArray(br)) {
    for (const item of br) {
      if (item == null || typeof item !== 'object' || Array.isArray(item)) continue;
      const o = item as Record<string, unknown>;
      const s = o.start_date ?? o.start;
      if (typeof s === 'string' && s.trim()) {
        const dt = new Date(s.trim());
        if (!Number.isNaN(dt.getTime())) {
          const iso = dt.toISOString();
          const pe = o as { event_id?: string; slug?: string; child_event_id?: string };
          let target: string | undefined;
          if (typeof pe.event_id === 'string' && pe.event_id.trim() && pe.event_id.trim() !== currentEventId) {
            target = pe.event_id.trim();
          } else if (typeof pe.child_event_id === 'string' && pe.child_event_id.trim()) {
            const c = pe.child_event_id.trim();
            if (c !== currentEventId) target = c;
          } else if (typeof pe.slug === 'string' && pe.slug.trim() && pe.slug.trim() !== currentSlug) {
            target = pe.slug.trim();
          }
          out.push({ iso, targetEventId: target });
        }
      }
    }
  }
  return out;
}

function normKey(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * `targetKey` is the slug, Mongo id, or public id the widget will pass to `GET /widget?event_id=`.
 * Returns true if `event` already is that event (no need to refetch).
 */
export function eventDocumentMatchesTargetKey(event: unknown, targetKey: string): boolean {
  if (!event || typeof event !== 'object') return false;
  const e = event as Record<string, unknown>;
  const d = e.details as Record<string, unknown> | undefined;
  const keys: string[] = [];
  for (const x of [e._id, e.event_id, e.slug, d?.slug]) {
    if (typeof x === 'string' && x.trim()) keys.push(normKey(x));
  }
  const t = normKey(targetKey);
  return keys.includes(t);
}

export function getEventOccurrenceEntries(event: unknown): EventOccurrenceEntry[] {
  if (!event || typeof event !== 'object') return [];
  const e = event as Record<string, unknown>;
  const d = e.details as Record<string, unknown> | undefined;

  const currentEventId =
    (typeof e._id === 'string' && e._id.trim()) ||
    (typeof e.event_id === 'string' && e.event_id.trim()) ||
    '';
  const currentSlug =
    (typeof e.slug === 'string' && e.slug.trim()) ||
    (d && typeof d.slug === 'string' && d.slug.trim() ? d.slug : '') ||
    '';

  const byDay = new Map<string, EventOccurrenceEntry>();

  const primary =
    typeof e.event_start_date === 'string' && e.event_start_date.trim()
      ? e.event_start_date.trim()
      : typeof d?.event_start_date === 'string' && String(d.event_start_date).trim()
        ? String(d.event_start_date).trim()
        : '';
  if (primary) {
    const dt = new Date(primary);
    if (!Number.isNaN(dt.getTime())) {
      mergeByDay(byDay, { iso: dt.toISOString() });
    }
  }

  const linkedRaw = e.linked_events ?? d?.linked_events;
  if (Array.isArray(linkedRaw)) {
    for (const item of linkedRaw) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      const dateStr = typeof row.date === 'string' ? row.date.trim() : '';
      const targetSegment = linkedEventUrlSegment(row);
      if (!dateStr) continue;
      if (!targetSegment) continue;
      if (currentEventId && targetSegment === currentEventId) continue;
      if (currentSlug && targetSegment === currentSlug) continue;
      const dt = new Date(dateStr);
      if (Number.isNaN(dt.getTime())) continue;
      mergeByDay(byDay, { iso: dt.toISOString(), targetEventId: targetSegment });
    }
  }

  for (const o of collectObjectOccurrenceRows(e, currentEventId, currentSlug)) {
    mergeByDay(byDay, o);
  }

  for (const iso of mergeEventSources(e)) {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) continue;
    mergeByDay(byDay, { iso: dt.toISOString() });
  }

  const entries = [...byDay.values()];
  entries.sort((a, b) => new Date(a.iso).getTime() - new Date(b.iso).getTime());
  return entries;
}

/** True when the occurrence’s start is strictly before “now” (no grace — sell cutoff). */
export function isOccurrenceStartInPast(iso: string): boolean {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return true;
  return t < Date.now();
}

/** Excludes past occurrence rows so users cannot pick (or see as default) dates that have already started. */
export function getPurchasableOccurrenceEntries(entries: EventOccurrenceEntry[]): EventOccurrenceEntry[] {
  return entries.filter((e) => !isOccurrenceStartInPast(e.iso));
}

/**
 * Single place for what to show in headers / hero / date chip when `selectedOccurrenceIso` is missing or invalid.
 * Prefers a still-valid selection, then the earliest future purchasable occurrence, then `event_start_date`, then the first known row.
 */
export function resolveDisplayOccurrenceIso(event: unknown, selectedIso: string | null): string {
  const all = getEventOccurrenceEntries(event);
  const purchasable = getPurchasableOccurrenceEntries(all);
  if (selectedIso && purchasable.some((e) => e.iso === selectedIso)) {
    return selectedIso;
  }
  if (purchasable[0]) {
    return purchasable[0].iso;
  }
  const e = event as { event_start_date?: string };
  if (typeof e?.event_start_date === 'string' && e.event_start_date.trim()) {
    return new Date(e.event_start_date.trim()).toISOString();
  }
  return all[0]?.iso ?? '';
}

/**
 * Resolves the initial `selectedOccurrenceIso` on SET_DATA. Prefer `override` if it is still a purchasable date.
 */
export function resolveSelectedOccurrenceIsoOnLoad(
  event: unknown,
  override: string | null | undefined,
): string | null {
  const all = getEventOccurrenceEntries(event);
  const purchasable = getPurchasableOccurrenceEntries(all);
  if (override && purchasable.some((e) => e.iso === override)) {
    return override;
  }
  return purchasable[0]?.iso ?? null;
}
